import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Landmark, ArrowLeft, RefreshCw, Send, Download, FileText, Repeat, CheckCircle2, Loader2, Search, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

export default function AdminTaxProgram() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");
  const [msg, setMsg] = useState(null);
  const [autoEfax, setAutoEfax] = useState(null);
  const [irsFax, setIrsFax] = useState("");
  const [irsOptions, setIrsOptions] = useState([]);

  const token = localStorage.getItem("th_admin_token");
  const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try { const r = await axios.get(`${API}/admin/tax-credit/clients`, cfg); setClients(r.data.clients || []); }
    catch (e) { setErr(e.response?.data?.detail || "Could not load program clients."); }
    finally { setLoading(false); }
    try { const s = await axios.get(`${API}/admin/tax-credit/settings`, cfg); setAutoEfax(!!s.data.auto_efax); setIrsFax(s.data.irs_fax || ""); } catch { /* */ }
    // Auto-populate the IRS fax destinations from the verified rolodex.
    try {
      const d = await axios.get(`${API}/admin/fax/directory`, cfg);
      const opts = [];
      (d.data.directory || []).forEach((grp) => (grp.entries || []).forEach((e) => {
        const lbl = e.label || "";
        if (/\bIRS\b/i.test(lbl) || /treasury\s*&\s*agencies/i.test(grp.category || "")) {
          if (/\bIRS\b/i.test(lbl)) opts.push({ label: lbl, fax: e.fax });
        }
      }));
      setIrsOptions(opts);
      setIrsFax((cur) => cur || (opts[0] && opts[0].fax) || "");
    } catch { /* */ }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveIrsFax = async (fax) => {
    setIrsFax(fax);
    try {
      await axios.post(`${API}/admin/tax-credit/settings`, { irs_fax: fax }, cfg);
      setMsg({ ok: true, text: `IRS fax destination set to ${fax} — used for every auto-filed and manual eFax.` });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Could not save the IRS fax." }); }
  };

  const toggleAutoEfax = async () => {
    const next = !autoEfax;
    setAutoEfax(next);
    try {
      await axios.post(`${API}/admin/tax-credit/settings`, { auto_efax: next }, cfg);
      setMsg({ ok: true, text: next ? "Auto-eFax ON — each client's IRS forms fire to the IRS the moment they enroll (live fax)." : "Auto-eFax OFF — nothing sends automatically; file with the eFax button when ready." });
    } catch (e) { setAutoEfax(!next); setMsg({ ok: false, text: e.response?.data?.detail || "Could not update setting." }); }
  };

  const downloadPackage = async (appId, form) => {
    setBusyId(appId + (form || "")); setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/tax-credit/bulk-pdf`, { app_ids: [appId], form: form || null }, { ...cfg, responseType: "blob" });
      const blob = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blob; a.download = form === "election" ? "sec6418_election_statement.pdf" : form === "certificate" ? "solar_tax_credit_certificate.pdf" : "tax_resolution_package.pdf";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(blob);
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Download failed." }); }
    finally { setBusyId(""); }
  };

  const efax = async (appId) => {
    setBusyId(appId + "efax"); setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/tax-credit/auto-efax`, { app_id: appId, to: irsFax || undefined }, cfg);
      setMsg({ ok: r.data.ok, text: r.data.ok ? `✓ Package eFaxed to IRS (${r.data.efax?.to}). Status: ${r.data.efax?.status}` : `eFax failed: ${r.data.efax?.error || "unknown"}` });
      load();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "eFax failed." }); }
    finally { setBusyId(""); }
  };

  const chargeMonthly = async (appId) => {
    setBusyId(appId + "charge"); setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/tax-credit/charge-monthly`, { app_id: appId }, cfg);
      setMsg({ ok: r.data.ok, text: r.data.ok ? "✓ $9.99 monthly service fee charged." : `Monthly charge not completed: ${r.data.result?.status}${r.data.result?.error ? " — " + r.data.result.error : ""}` });
      load();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Charge failed." }); }
    finally { setBusyId(""); }
  };

  const filtered = clients.filter((c) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [c.borrower_name, c.email, c.tin, c.tax_years].some((v) => String(v || "").toLowerCase().includes(s));
  });

  const paidCount = clients.filter((c) => c.paid).length;
  const totalDebt = clients.reduce((a, c) => a + (Number(c.tax_debt) || 0), 0);

  return (
    <div style={{ background: "#06160F", minHeight: "100vh", color: "#E7F3EE", fontFamily: "'Manrope',system-ui,sans-serif" }} data-testid="admin-tax-program-page">
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #0B3D2E", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => nav("/admin")} data-testid="atp-back" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#6FD3AE", fontSize: 12, cursor: "pointer" }}><ArrowLeft size={14} /> Admin</button>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", fontSize: 14, color: "#E7F3EE" }}><Landmark size={18} color="#6FD3AE" /> Tax Debt Assistance Program</span>
        </div>
        <button onClick={() => window.open("/tax-flyer", "_blank")} data-testid="atp-flyer" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#E4C97A", border: "1px solid #E4C97A", color: "#06160F", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer", marginRight: 8 }}><Megaphone size={13} /> Marketing Flyer</button>
        <button onClick={load} data-testid="atp-refresh" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid #14503B", color: "#6FD3AE", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}><RefreshCw size={13} /> Refresh</button>
      </header>

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "22px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14, background: "#0A241A", border: "1px solid #14503B", borderRadius: 10, padding: "10px 14px" }}>
          <div style={{ fontSize: 12.5, color: "#C8DAD2" }}>
            <strong style={{ color: "#6FD3AE" }}>Auto-eFax IRS forms on signup</strong>
            <span style={{ color: "#7FA496", marginLeft: 8 }}>{autoEfax === false ? "Off — nothing is sent automatically; use the eFax button to file manually." : "On — a client's selected IRS forms fire to the IRS automatically the moment they enroll (live fax)."}</span>
          </div>
          <button onClick={toggleAutoEfax} data-testid="atp-autoefax-toggle"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", background: "transparent", border: "none" }}>
            <span style={{ width: 42, height: 22, borderRadius: 999, background: autoEfax ? "#6FD3AE" : "#2a1a07", border: `1px solid ${autoEfax ? "#6FD3AE" : "#7a5a12"}`, position: "relative", transition: "background .2s" }}>
              <span style={{ position: "absolute", top: 2, left: autoEfax ? 22 : 2, width: 16, height: 16, borderRadius: "50%", background: autoEfax ? "#06160F" : "#E4C97A", transition: "left .2s" }} />
            </span>
            <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", color: autoEfax ? "#6FD3AE" : "#E4C97A" }}>{autoEfax ? "Auto" : "Review"}</span>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14, background: "#0A241A", border: "1px solid #14503B", borderRadius: 10, padding: "10px 14px" }}>
          <div style={{ fontSize: 12.5, color: "#C8DAD2", flex: "1 1 240px" }}>
            <strong style={{ color: "#6FD3AE" }}>IRS fax destination</strong>
            <span style={{ color: "#7FA496", marginLeft: 8 }}>Auto-populated from the verified fax rolodex — used for every eFax to the IRS.</span>
          </div>
          <select value={irsFax} onChange={(e) => saveIrsFax(e.target.value)} data-testid="atp-irs-fax-select"
            style={{ background: "#06160F", color: "#E7F3EE", border: "1px solid #14503B", borderRadius: 8, padding: "9px 12px", fontSize: 12.5, minWidth: 320 }}>
            {irsOptions.length === 0 && <option value="">Loading verified IRS fax numbers…</option>}
            {!irsOptions.some((o) => o.fax === irsFax) && irsFax && <option value={irsFax}>{irsFax} (current)</option>}
            {irsOptions.map((o) => (
              <option key={o.fax + o.label} value={o.fax}>{o.label} — {o.fax}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
          <Stat label="Enrolled clients" value={clients.length} />
          <Stat label="Paid" value={paidCount} />
          <Stat label="Total IRS debt" value={money(totalDebt)} />
        </div>

        <div style={{ position: "relative", marginBottom: 14, maxWidth: 420 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: 11, color: "#5f7a6d" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="atp-search" placeholder="Search name, email, TIN, tax year…"
            style={{ width: "100%", background: "#0A241A", border: "1px solid #14503B", borderRadius: 8, padding: "9px 12px 9px 34px", color: "#E7F3EE", fontSize: 13, outline: "none" }} />
        </div>

        {msg && <div data-testid="atp-msg" style={{ background: msg.ok ? "#07251a" : "#2a1212", border: `1px solid ${msg.ok ? "#14503B" : "#7A3A3A"}`, color: msg.ok ? "#6FD3AE" : "#FF9A9A", padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{msg.text}</div>}
        {err && <div style={{ color: "#FF9A9A", marginBottom: 14 }}>{err}</div>}

        {loading ? <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#9DB8AD" }}><Loader2 className="animate-spin" size={16} /> Loading…</div>
          : filtered.length === 0 ? <div style={{ color: "#9DB8AD", padding: 30, textAlign: "center" }}>No program clients yet. Enrollments from the Tax Debt Assistance Program appear here.</div>
          : (
          <div style={{ overflowX: "auto", border: "1px solid #123c2c", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }} data-testid="atp-table">
              <thead>
                <tr style={{ background: "#0A241A", textAlign: "left", color: "#7FA496", textTransform: "uppercase", fontSize: 10.5, letterSpacing: ".06em" }}>
                  <Th>Client</Th><Th>IRS Debt</Th><Th>Fee</Th><Th>Payment</Th><Th>Monthly $9.99</Th><Th>IRS eFax</Th><Th>Documents</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.app_id} data-testid={`atp-row-${c.app_id}`} style={{ borderTop: "1px solid #123c2c" }}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: "#E7F3EE" }}>{c.borrower_name || "—"}</div>
                      <div style={{ color: "#7FA496", fontSize: 11 }}>{c.email}</div>
                      <div style={{ color: "#5f7a6d", fontSize: 10.5 }}>TIN {c.tin || "—"} · Yrs {c.tax_years || "—"}</div>
                    </td>
                    <td style={td}>{money(c.tax_debt)}</td>
                    <td style={{ ...td, color: "#6FD3AE", fontWeight: 700 }}>{money(c.fee)}</td>
                    <td style={td}>
                      {c.paid ? <span style={pill("#07251a", "#6FD3AE")}><CheckCircle2 size={12} /> Paid {c.paid_fee ? money(c.paid_fee) : ""}</span>
                              : <span style={pill("#241a07", "#E4C97A")}>Unpaid</span>}
                    </td>
                    <td style={td}>
                      {c.monthly_billing?.enrolled
                        ? <span style={pill("#07251a", "#6FD3AE")}><CheckCircle2 size={12} /> •••• {c.monthly_billing.card_last4 || "card"}{c.monthly_billing.last_period ? ` · ${c.monthly_billing.last_period}` : ""}</span>
                        : <span style={pill("#241a07", "#E4C97A")}>No card</span>}
                    </td>
                    <td style={td}>
                      {c.efax_status ? <span style={pill(c.efax_status === "failed" ? "#2a1212" : c.efax_status === "held_for_review" ? "#241a07" : "#07251a", c.efax_status === "failed" ? "#FF9A9A" : c.efax_status === "held_for_review" ? "#E4C97A" : "#6FD3AE")}>{c.efax_status === "held_for_review" ? "review" : c.efax_status}</span>
                                     : <span style={{ color: "#5f7a6d" }}>—</span>}
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <ActBtn onClick={() => nav(`/tax-credit-transfer/${c.app_id}`)} tid={`atp-build-${c.app_id}`} icon={Landmark} label="Builder" />
                      <ActBtn onClick={() => nav(`/mortgage-replacement/${c.app_id}`)} tid={`atp-mtg-${c.app_id}`} icon={Repeat} label="Mtg" />
                      <ActBtn onClick={() => downloadPackage(c.app_id, "")} tid={`atp-dl-${c.app_id}`} icon={Download} label="PDF" busy={busyId === c.app_id} />
                      <ActBtn onClick={() => downloadPackage(c.app_id, "election")} tid={`atp-election-${c.app_id}`} icon={FileText} label="Election" busy={busyId === c.app_id + "election"} />
                      <ActBtn onClick={() => downloadPackage(c.app_id, "certificate")} tid={`atp-cert-${c.app_id}`} icon={FileText} label="Certificate" busy={busyId === c.app_id + "certificate"} />
                      <ActBtn onClick={() => chargeMonthly(c.app_id)} tid={`atp-charge-${c.app_id}`} icon={Repeat} label="Charge $9.99" busy={busyId === c.app_id + "charge"} disabled={!c.monthly_billing?.enrolled} />
                      <ActBtn onClick={() => efax(c.app_id)} tid={`atp-efax-${c.app_id}`} icon={Send} label="eFax IRS" busy={busyId === c.app_id + "efax"} accent />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Th = ({ children }) => <th style={{ padding: "10px 12px", fontWeight: 700 }}>{children}</th>;
const td = { padding: "10px 12px", verticalAlign: "top", color: "#C8DAD2" };
const pill = (bg, color) => ({ display: "inline-flex", alignItems: "center", gap: 5, background: bg, color, border: `1px solid ${color}44`, padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700 });
const Stat = ({ label, value }) => (
  <div style={{ background: "#0A241A", border: "1px solid #14503B", borderRadius: 12, padding: "14px 20px", minWidth: 150 }}>
    <div style={{ fontSize: 10.5, color: "#7FA496", textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "Georgia,serif", color: "#6FD3AE", marginTop: 3 }}>{value}</div>
  </div>
);
const ActBtn = ({ onClick, tid, icon: Icon, label, busy, accent, disabled }) => (
  <button onClick={onClick} data-testid={tid} disabled={busy || disabled}
    style={{ display: "inline-flex", alignItems: "center", gap: 5, margin: "2px 3px", opacity: disabled ? 0.5 : 1, background: accent ? "#6FD3AE" : "transparent", color: accent ? "#06160F" : "#6FD3AE", border: `1px solid ${accent ? "#6FD3AE" : "#14503B"}`, padding: "6px 10px", borderRadius: 7, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", cursor: disabled ? "not-allowed" : "pointer" }}>
    {busy ? <Loader2 className="animate-spin" size={11} /> : <Icon size={11} />} {label}
  </button>
);
